import { yupResolver } from "@hookform/resolvers/yup";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as yup from "yup";
import { MessageTemplate } from "../../../models/Candidate";
import { NotificationTemplate } from "../../../models/OrgNotificationSetting";
import { useBulkSendCandidateMessage } from "../../../networking/candidate.hooks";
import { useOrganisationNotificationTemplates } from "../../../networking/organisation.hooks";
import AppInput from "../../Core/AppInput/AppInput";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import Dropdown from "../../Core/Dropdown/Dropdown";
import Modal from "../../Core/Modal/Modal";
import TextArea from "../../Core/TextArea/TextArea";
import { useAuthStore } from "../../../stores/auth.store";

const SendMessageSchema = yup.object().shape({
	message: yup.string().required("FIELD_IS_REQUIRED"),
	subject: yup.string().required("FIELD_IS_REQUIRED"),
});

type SendMessageRequest = { message: string; subject: string };

interface BulkSendMessageModalProps {
	hideModal: Function;
	userIds: string[];
	recipientNames?: string[];
}

const BulkSendMessageModal: React.FC<BulkSendMessageModalProps> = ({
	hideModal,
	userIds,
	recipientNames = [],
}) => {
	const { t } = useTranslation();
	const authStore = useAuthStore();

	const { mutate: bulkSendMessage } = useBulkSendCandidateMessage();
	const { data: messageTemplates } = useOrganisationNotificationTemplates({
		tag: "recruitment",
	});

	const { recruitmentTemplates, recruitmentFooter } = useMemo(
		() =>
			messageTemplates.templates.reduce<{
				recruitmentTemplates: NotificationTemplate[];
				recruitmentFooter?: NotificationTemplate;
			}>(
				(acc, template) => {
					if (template.name === "RECRUITMENT_FOOTER") {
						acc.recruitmentFooter = template;
					} else {
						acc.recruitmentTemplates.push(template);
					}
					return acc;
				},
				{ recruitmentTemplates: [], recruitmentFooter: undefined }
			),
		[messageTemplates]
	);

	let templates: MessageTemplate[] = [{ id: "", name: "- " + t("USE_TEMPLATE") + " -" }];

	if (recruitmentTemplates.length > 0) {
		templates = [
			...templates,
			...recruitmentTemplates.map((t) => ({
				id: t.id,
				name: t.description || t.name,
				subject: t.emailSubject || "",
				body: t.body || "",
			})),
		].sort((a, b) => a.name.localeCompare(b.name));
	}

	const [template, setTemplate] = useState<MessageTemplate>(templates[0]);

	const { register, handleSubmit, formState, setValue } = useForm<SendMessageRequest>({
		mode: "onBlur",
		resolver: yupResolver(SendMessageSchema),
	});

	const changeTemplate = (template: MessageTemplate) => {
		setValue("subject", template?.subject || "");
		// Strip {{firstName}} — backend compiles per-recipient before fanning out notifications
		const body = (template.body || "") + (recruitmentFooter?.body || "");
		setValue("message", compileTemplate(body));
		setTemplate(template);
	};

	const changeSubject = (e: any) => {
		setValue("subject", e.target.value);
	};

	const changeMessage = (e: any) => {
		setValue("message", e.target.value);
	};

	const onSubmit = handleSubmit(async (e) => {
		await bulkSendMessage({ userIds, message: e.message, subject: e.subject });
		hideModal();
	});

	function compileTemplate(body: string) {
		// Only substitute authUserName client-side; {{firstName}} stays for backend per-recipient compilation
		return body.replace("{{authUserName}}", authStore.loggedInUserName || "");
	}

	const recipientSummary =
		recipientNames.length > 0
			? recipientNames.slice(0, 3).join(", ") +
			  (recipientNames.length > 3 ? ` +${recipientNames.length - 3}` : "")
			: `${userIds.length}`;

	return (
		<form onSubmit={onSubmit}>
			<Modal maxWidth="46rem" padding="1.375rem 3.125rem 3.125rem 3.125rem">
				<MessageHeader>
					<AppTextWrapper>
						<AppText size="xm" weight="bold">
							{t("TO")}:{" "}
						</AppText>
						<AppText size="xm" weight="bold">
							{userIds.length} {t("recruitment.bulk.recipients")}
						</AppText>
						{recipientNames.length > 0 && (
							<AppText size="m" margin="0 0 0 0.5rem" color="grayText">
								({recipientSummary})
							</AppText>
						)}
					</AppTextWrapper>

					<Dropdown<MessageTemplate, MessageTemplate>
						width="13rem"
						getValue={(o) => o}
						onChange={changeTemplate}
						value={template}
						options={templates}
						getOptionLabel={(template: MessageTemplate) => t(template.name as any)}
						margin="0"
						padding="0"
					/>
				</MessageHeader>
				<AppInput
					{...register("subject")}
					onChange={changeSubject}
					placeholder="recruitment.sendMessage.subject"
					containerWidth="100%"
					containerMargin="0 0 0.5rem 0"
					margin="0"
					padding="0 0.75rem 0 0.75rem"
					errors={formState.errors.subject?.message}
				/>
				<TextAreaWrapper>
					<TextArea
						{...register("message")}
						onChange={changeMessage}
						placeholder="recruitment.sendMessage.message"
						name="message"
						width="100%"
						height="17rem"
						errors={formState.errors.message?.message}
					/>
				</TextAreaWrapper>
				<ButtonRow>
					<Button
						disabled={formState.isSubmitting}
						width="150px"
						margin="1.125rem 0 0 0"
						fontSize="xms"
						loading={formState.isSubmitting}
					>
						{t("SEND")}
					</Button>
					<Button
						onClick={() => hideModal()}
						width="150px"
						margin="1.125rem 0 0 0"
						textColor="black"
						backgroundColor="buttonGray"
						fontSize="s"
					>
						{t("SEND_MESSAGE_CANCEL")}
					</Button>
				</ButtonRow>
			</Modal>
		</form>
	);
};
export default BulkSendMessageModal;

const MessageHeader = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.625rem;
`;

const AppTextWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.25rem;
`;

const ButtonRow = styled.div`
	display: flex;
	gap: 0.75rem;
`;

const TextAreaWrapper = styled.div`
	width: 100%;
`;
